import { FinancialAccountSourceType } from "@common/types/financialAccount";
import { isBefore, subWeeks } from "date-fns";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Taxonomy } from "../categories";
import { CURRENT_YEAR_TAX_RULES } from "../taxSavingRules";
import { TransactionBase, TransactionStatus } from "../types/transaction";
import { getAccountNameWithLast4 } from "./financialAccount";

export interface TransactionOrigin {
    source: FinancialAccountSourceType;
    bankDescription: string;
    date: Date;
}

export interface InvoiceOrBillMatchFields {
    invoice?: object | null;
    bill?: object | null;
    invoiceId?: string | null;
    billId?: string | null;
}

/**
 * True when any of the transaction's cash-basis matches (`transaction_match`)
 * or accrual-review matches (`accrual_match`) point at an invoice or bill.
 * Owned fields (copy + lock) live in
 * `matched-document-transaction-fields.ts` — this predicate is only the
 * "is this transaction matched to a document?" check.
 *
 * Checks the `invoiceId` / `billId` FK columns as well as the loaded
 * `invoice` / `bill` relations: grid/list surfaces only select the FK columns
 * on `transaction_match` (the full relations load solely under
 * `includeMatchDetails`), while the transaction details view loads the
 * relations. Accrual-review matches expose `invoice` / `bill` on
 * `accrualMatches` when `includeAccrualMatchDetails` is on (the default on
 * accrual workspaces). Checking either keeps the lock consistent across
 * every surface. Transfer-pair accrual matches (paired transaction only)
 * do not lock.
 *
 * Typed structurally so cash-basis matches (`TransactionMatch` /
 * `TransactionMatchSchema`) and accrual-review matches (`AccrualMatchDisplay`)
 * can be passed together — they share invoice/bill fields but not a common
 * element type.
 */
export function isMatchedToInvoiceOrBill(
    ...matchLists: Array<InvoiceOrBillMatchFields[] | null | undefined>
): boolean {
    return matchLists.some((list) =>
        (list ?? []).some(
            (m) => !!m.invoice || !!m.bill || !!m.invoiceId || !!m.billId,
        ),
    );
}

export const MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE =
    "This split was created by an invoice or bill match. Unmatch to combine the transactions again.";

export function isTransactionFamilyMatchedToInvoiceOrBill(transaction: {
    transactionMatches?: InvoiceOrBillMatchFields[] | null;
    accrualMatches?: InvoiceOrBillMatchFields[] | null;
    splits?: Array<{
        transactionMatches?: InvoiceOrBillMatchFields[] | null;
        accrualMatches?: InvoiceOrBillMatchFields[] | null;
    } | null> | null;
}): boolean {
    return isMatchedToInvoiceOrBill(
        transaction.transactionMatches,
        transaction.accrualMatches,
        ...(transaction.splits ?? []).flatMap((split) => [
            split?.transactionMatches,
            split?.accrualMatches,
        ]),
    );
}

export const allCategories = Object.values(Taxonomy);

export const taxonomiesExcludedFromAmountSavedCalculation: Taxonomy[] =
    Object.values(Taxonomy).filter(
        (category) => !CURRENT_YEAR_TAX_RULES[category],
    );

export function getTransactionAccountName(
    transaction: TransactionListItem,
): string {
    return transaction.financialAccount.name ?? "";
}

export function getTransactionAccountNameWithLast4(
    transaction: TransactionListItem,
    accountNumberMaskLength = 4,
): string {
    const name = getTransactionAccountName(transaction);
    const account = transaction.financialAccount;

    return account
        ? getAccountNameWithLast4(account, accountNumberMaskLength)
        : name;
}

export enum TransactionCoaFilterType {
    USER = "user",
    RULE = "rule",
    KICK = "kick",
}

export const DELETABLE_ORIGIN_SOURCES = [
    FinancialAccountSourceType.MANUAL,
    FinancialAccountSourceType.FILE_IMPORT,
    FinancialAccountSourceType.GUSTO,
    FinancialAccountSourceType.CHECK,
    FinancialAccountSourceType.DEPOSIT,
];

export const RECONCILIATION_TRANSACTION_KEYWORD = "Reconciliation transaction";

export function canTransactionBeDeleted(
    transaction: TransactionBase,
    origins: TransactionOrigin[],
) {
    if (
        transaction.status === TransactionStatus.PENDING &&
        isBefore(transaction.date, subWeeks(new Date(), 1))
    ) {
        return true;
    }

    const originsAllowToDelete = origins.every((origin) =>
        DELETABLE_ORIGIN_SOURCES.includes(origin.source),
    );
    if (originsAllowToDelete) {
        const gustoOrigin = origins.find(
            (origin) => origin.source === FinancialAccountSourceType.GUSTO,
        );

        if (gustoOrigin) {
            // NOTE: this hack is used because right now we don't create separate
            // integration account with sourceType = manual for manually created
            // reconciliation transactions
            return gustoOrigin.bankDescription
                .toLowerCase()
                .includes(RECONCILIATION_TRANSACTION_KEYWORD.toLowerCase());
        }

        return true;
    }

    return false;
}

export function getNonFinancialJournalEntryIndex(
    transaction: Pick<TransactionBase, "amount">,
): number {
    return transaction.amount >= 0 ? 1 : 0;
}

export function getFinancialJournalEntryIndex(
    transaction: Pick<TransactionBase, "amount">,
): number {
    return transaction.amount < 0 ? 1 : 0;
}

type TransactionForGeneralLedgerAccountResolution = Pick<
    TransactionBase,
    "amount" | "accountId" | "entityId"
> & {
    journalEntries?: Array<{
        accountId: string;
        lineOrder: number;
        accountingLedger?: {
            entityId: number;
            basis: AccountingLedgerBasis;
        };
    }> | null;
};

export function getEffectiveGeneralLedgerAccountId(
    transaction: TransactionForGeneralLedgerAccountResolution,
): string | null {
    if (transaction.accountId) {
        return transaction.accountId;
    }

    const relevantJournalEntries = (transaction.journalEntries ?? [])
        .filter(
            (journalEntry) =>
                journalEntry.accountingLedger?.entityId ===
                    transaction.entityId &&
                journalEntry.accountingLedger?.basis ===
                    AccountingLedgerBasis.cash,
        )
        .sort((a, b) => a.lineOrder - b.lineOrder);

    return (
        relevantJournalEntries[getNonFinancialJournalEntryIndex(transaction)]
            ?.accountId ?? null
    );
}

type TransactionForAccrualLedgerAccountResolution = Pick<
    TransactionBase,
    "amount" | "accrualAccountId" | "entityId"
> & {
    journalEntries?: Array<{
        accountId: string;
        lineOrder: number;
        accountingLedger?: {
            entityId: number;
            basis: AccountingLedgerBasis;
        };
    }> | null;
};

export function getEffectiveAccrualLedgerAccountId(
    transaction: TransactionForAccrualLedgerAccountResolution,
): string | null {
    const relevantJournalEntries = (transaction.journalEntries ?? [])
        .filter(
            (journalEntry) =>
                journalEntry.accountingLedger?.entityId ===
                    transaction.entityId &&
                journalEntry.accountingLedger?.basis ===
                    AccountingLedgerBasis.accruals,
        )
        .sort((a, b) => a.lineOrder - b.lineOrder);

    return (
        relevantJournalEntries[getNonFinancialJournalEntryIndex(transaction)]
            ?.accountId ??
        transaction.accrualAccountId ??
        null
    );
}
